import type { DocSearchRecord } from './docs-search-types';
import type { DocNode } from './docs-tree-utils';

import type { TLNodeSummary } from '@/api/gen/tessera-backend-api.schemas';
import { LOOM_NODES_SLUG_PATH, loomNodeCategorySlug, loomNodeDocSlug } from '@/utils/doc-keys';
import { isTruthy } from '@/utils/filter';

/** The authored page the fetched nodes hang under: `content/loom/nodes/index.mdx`. */
const [LOOM_SLUG, NODES_SLUG] = LOOM_NODES_SLUG_PATH.split('/');

/**
 * Whether the reader can see the section the node pages hang under — the cue for
 * saying why it holds no pages, rather than saying it to a reader who has no
 * business with workflows at all.
 *
 * @param tree - A role-filtered tree from `visibleTree`.
 * @returns `true` when the Loom section survived filtering.
 */
export function showsLoomNodeSection(tree: DocNode[]): boolean {
  return tree.some((node) => node.slug === LOOM_SLUG);
}

/** Section headings a node page renders, in the order it renders them.
 * Examples deliberately precede the field reference: a filled-in node orients a
 * first-time reader faster than the field list does, and the TOC rail keeps
 * Inputs one click away for returning readers. */
export const LOOM_NODE_DOC_SECTIONS = {
  whenToUse: { id: 'when-to-use-this', text: 'When to use this', level: 2 },
  preconditions: { id: 'before-you-start', text: 'Before you start', level: 2 },
  examples: { id: 'examples', text: 'Examples', level: 2 },
  inputs: { id: 'inputs', text: 'Inputs', level: 2 },
  outputs: { id: 'outputs', text: 'Outputs', level: 2 },
  caveats: { id: 'things-to-watch', text: 'Things to watch', level: 2 },
  commonErrors: { id: 'common-errors', text: 'Common errors', level: 2 },
} as const satisfies Record<string, { id: string; text: string; level: 2 }>;

function nodeLeaf(node: TLNodeSummary): DocNode {
  return { slug: node.name, title: node.display_name, nodeName: node.name };
}

/**
 * Groups nodes into one section per category, each sorted by display name —
 * matching how `docs-build.ts` orders hand-authored siblings (no `order`
 * metadata, so alphabetical by title).
 */
function categorySections(nodes: TLNodeSummary[]): DocNode[] {
  const byCategory = new Map<string, TLNodeSummary[]>();
  for (const node of nodes) {
    const siblings = byCategory.get(node.category);
    if (siblings) siblings.push(node);
    else byCategory.set(node.category, [node]);
  }

  return [...byCategory.entries()]
    .map(([category, categoryNodes]) => ({
      slug: loomNodeCategorySlug(category),
      title: category,
      collapsed: true,
      children: [...categoryNodes].sort((a, b) => a.display_name.localeCompare(b.display_name)).map(nodeLeaf),
    }))
    .sort((a, b) => a.title.localeCompare(b.title));
}

/**
 * Returns a copy of the documentation tree with the registry's nodes attached
 * under the authored Workflow nodes page.
 *
 * Nodes are grouped into a section per category. The tree is returned unchanged
 * when the registry is empty — a failed or in-flight request leaves the authored
 * page standing on its own rather than blanking the section.
 *
 * @param tree - The static tree (`DOCS_TREE`).
 * @param nodes - Node summaries from `GET /api/v2/workflows/nodes`.
 * @returns A new tree; untouched branches are shared, not cloned.
 * @example
 * mergeLoomNodeDocs(DOCS_TREE, [{ name: 'http_request', category: 'Integrations', … }])
 * // → the Workflow nodes page now has an "Integrations" child with one node under it
 */
export function mergeLoomNodeDocs(tree: DocNode[], nodes: TLNodeSummary[]): DocNode[] {
  if (nodes.length === 0) return tree;

  const sections = categorySections(nodes);

  return tree.map((loom) => {
    if (loom.slug !== LOOM_SLUG) return loom;
    return {
      ...loom,
      children: loom.children?.map((page) =>
        page.slug === NODES_SLUG ? { ...page, children: [...(page.children ?? []), ...sections] } : page,
      ),
    };
  });
}

/**
 * Search records for the node pages, which the build-time index cannot contain
 * because the pages do not exist until the registry answers.
 *
 * One record per node: enough to find a node by its name, tagline, description
 * or tags. Indexing each page's field reference would cost one request per node.
 *
 * @param nodes - Node summaries from `GET /api/v2/workflows/nodes`.
 * @returns Records shaped exactly like the build-time ones, safe to insert into the same index.
 */
export function loomNodeSearchRecords(nodes: TLNodeSummary[]): DocSearchRecord[] {
  return nodes.map((node) => ({
    id: loomNodeDocSlug(node.name, node.category),
    path: loomNodeDocSlug(node.name, node.category),
    headingId: '',
    title: node.display_name,
    headingText: '',
    text: [node.tagline, node.description, node.name, node.category, ...(node.tags ?? [])].filter(isTruthy).join(' '),
    // Not gated: these records only exist because the registry answered, and it
    // only answers for a viewer who may read workflows. `gated` would instead
    // send the filter to `resolveDocPath`, which knows the static tree only.
    gated: false,
  }));
}

/**
 * Identity of the fetched half of the tree, so a cached search index or memo is
 * rebuilt when the registry's answer changes but not on every refetch.
 *
 * @param nodes - Node summaries from `GET /api/v2/workflows/nodes`.
 * @returns A key that changes only when the set of node names changes.
 */
export function loomNodesKey(nodes: TLNodeSummary[]): string {
  return nodes
    .map((node) => node.name)
    .sort((a, b) => a.localeCompare(b))
    .join(',');
}
